<template>
  <div>
    <!-- Logo in top left corner -->
    <NuxtLink to="/">
      <div class="auth-logo d-flex align-center gap-x-3">
        <img :src="logoSrc" :alt="applicationName + ' Logo'" height="100" />
      </div>
    </NuxtLink>

    <v-row no-gutters class="auth-wrapper bg-surface">
      <!-- Left Side - Mountain Background Image -->
      <v-col md="8" class="d-none d-md-flex">
        <div class="position-relative bg-background w-100 me-0">
          <v-img cover height="100%" class="auth-footer-mask" :src="backgroundImage" :alt="applicationName + ' Background'" />
        </div>
      </v-col>

      <!-- Right Side - Login Form -->
      <v-col cols="12" md="4" class="auth-card-v2 d-flex align-center justify-center">
        <v-card flat :max-width="500" class="mt-12 mt-sm-0 pa-4">
          <v-card-text>
            <div class="d-flex align-center text-center justify-center">
              <h4 class="text-h5 mb-1">
                Welcome to <span class="text-h4 text-primary font-weight-black">{{ applicationName }}</span> 🚀
              </h4>
            </div>

            <!-- Search For Account (Step 1) -->
            <template v-if="!account">
              <p class="mb-0 mt-4">Let's Find Your {{ applicationName }} Account</p>
              <v-form ref="refVForm" v-model="valid" @submit.prevent="accountLoginOptions">
                <v-text-field v-model="email" class="mt-4" label="Email" type="email" prepend-inner-icon="mdi-email" autofocus clearable :rules="emailRules" :error-messages="errors.email" />
                <v-btn :loading="loading" class="mt-4" block type="submit" color="primary"> Get Account </v-btn>
              </v-form>
            </template>

            <!-- Login Selection Options (Step 2) -->
            <template v-if="account">
              <div class="text-center text-subtitle-1 font-weight-thin mt-4">
                <strong>SELECT A LOGIN METHOD FOR:</strong>
              </div>
              <div class="text-center text-subtitle-1 font-weight-thin text-primary">
                <strong>{{ email }}</strong>
                <v-btn density="compact" variant="text" icon="mdi-close-circle" @click="resetAccount" />
              </div>

              <!-- Login Method Selection -->
              <div class="mt-4">
                <v-row>
                  <v-col v-for="option in loginSelectionOptions()" :key="option.value" :cols="loginSelectionOptions().length === 1 ? 12 : 6">
                    <v-card :class="['login-option-card', { selected: loginSelection === option.value }]" :disabled="option.disabled" @click="selectLoginMethod(option.value)">
                      <v-card-text class="d-flex flex-column align-center gap-2 w-100 pa-4">
                        <v-icon :icon="getLoginIcon(option.value)" size="70" :color="loginSelection === option.value ? 'primary' : 'default'" />
                        <h6 class="text-h6 text-center">
                          {{ option.title }}
                        </h6>
                        <p class="text-sm text-center mb-0">
                          {{ option.desc }}
                        </p>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </template>

            <!-- OAuth Login (Step 3a) -->
            <template v-if="account && loginSelection === 'snowflakeOauth'">
              <v-btn :loading="loading" color="primary" variant="outlined" block class="mt-4" @click="snowflakeOauthLogin"> Login With Snowflake OAuth </v-btn>
            </template>

            <!-- Username/Password Login (Step 3b) -->
            <template v-if="account && loginSelection === 'applicationLogin'">
              <v-form ref="refPasswordForm" v-model="passwordFormValid" @submit.prevent="usernamePasswordLogin">
                <v-row>
                  <v-col cols="12">
                    <v-text-field v-model="email" class="mt-8" label="Email" type="email" prepend-inner-icon="mdi-email" autofocus clearable :rules="emailRules" :error-messages="errors.email" />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field v-model="password" label="Password" :type="showPassword ? 'text' : 'password'" prepend-inner-icon="mdi-lock" :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'" clearable :rules="passwordRules" :error-messages="errors.password" @click:append-inner="showPassword = !showPassword" />
                  </v-col>
                </v-row>
                <v-btn :loading="loading" class="mt-4" block type="submit" color="primary"> Login </v-btn>
              </v-form>
            </template>

            <!-- Warning/Error Messages -->
            <v-alert v-if="warningMessage" class="my-3" color="warning" density="compact" variant="outlined">
              <v-icon start>mdi-alert</v-icon>
              {{ warningMessage }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { useTheme } from 'vuetify'
import logoLight from '~/assets/images/logos/logo_snowflow_text_light.svg'
import logoDark from '~/assets/images/logos/logo_snowflow_text_dark.svg'
import mountainImage from '~/assets/images/illustrations/snowflow-mountain.jpg'

// Set page layout
definePageMeta({
  layout: false,
})

// Store Variables
const { applicationName } = storeToRefs(useGlobalsStore())

const theme = useTheme()
const router = useRouter()

// Reactive data
const valid = ref(false)
const passwordFormValid = ref(false)
const loading = ref(false)
const showPassword = ref(false)
const account = ref<string>('')
const warningMessage = ref<string | null>(null)
const email = ref<string>('')
const password = ref<string>('')
const loginSelection = ref<string>('')
const loginMethods = ref<string[]>([])

// Form errors
const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
})

// Computed properties
const isDark = computed(() => theme.global.name.value === 'dark')
const logoSrc = computed(() => (isDark.value ? logoDark : logoDark))
const backgroundImage = computed(() => mountainImage)

// Form validation rules
const emailRules = [(v: string) => !!v || 'Email is required', (v: string) => /.+@.+\..+/.test(v) || 'Email must be valid']

const passwordRules = [(v: string) => !!v || 'Password is required', (v: string) => v.length >= 6 || 'Password must be at least 6 characters']

// Methods
const loginSelectionOptions = () => {
  const options = []

  if (loginMethods.value.includes('snowflakeOauth')) {
    options.push({
      disabled: false,
      title: 'Snowflake OAuth',
      desc: 'Login to ' + applicationName.value + ' using your Snowflake credentials',
      value: 'snowflakeOauth',
    })
  }

  if (loginMethods.value.includes('applicationLogin')) {
    options.push({
      disabled: false,
      title: applicationName.value + ' Login',
      desc: 'Login to ' + applicationName.value + ' using your username & password',
      value: 'applicationLogin',
    })
  }

  // Default options for demo
  if (options.length === 0) {
    options.push({
      disabled: false,
      title: applicationName.value + ' Login',
      desc: 'Login to ' + applicationName.value + ' using your username & password',
      value: 'applicationLogin',
    })
  }

  return options
}

const getLoginIcon = (method: string) => {
  switch (method) {
    case 'snowflakeOauth':
      return 'mdi-snowflake'
    case 'applicationLogin':
      return 'mdi-account-circle'
    default:
      return 'mdi-login'
  }
}

const selectLoginMethod = (method: string) => {
  loginSelection.value = method
  warningMessage.value = null
}

const resetAccount = () => {
  account.value = ''
  warningMessage.value = null
  loginMethods.value = []
  loginSelection.value = ''
}

const accountLoginOptions = async () => {
  try {
    loading.value = true
    warningMessage.value = null

    // Simulate API call to get account login options
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // For demo purposes, set up mock login methods
    account.value = email.value
    loginMethods.value = ['applicationLogin'] // Could also include 'snowflakeOauth'
    loginSelection.value = 'applicationLogin'

    loading.value = false
  } catch (error) {
    loading.value = false
    warningMessage.value = 'Error getting account login options'
  }
}

const snowflakeOauthLogin = async () => {
  loading.value = true
  warningMessage.value = null

  try {
    // TODO: Implement Snowflake OAuth logic
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // For demo - simulate successful OAuth
    router.push('/')
  } catch (error) {
    warningMessage.value = 'Snowflake OAuth login failed'
  } finally {
    loading.value = false
  }
}

const usernamePasswordLogin = async () => {
  loading.value = true
  warningMessage.value = null

  try {
    // Simulate login API call
    await new Promise((resolve) => setTimeout(resolve, 1500))

    // Demo login - replace with actual authentication
    if (email.value === 'admin@snowflow.com' && password.value === 'password') {
      router.push('/')
    } else {
      throw new Error('Invalid credentials')
    }
  } catch (error) {
    warningMessage.value = 'Login was not successful. Check your credentials and try again.'
  } finally {
    loading.value = false
  }
}

// Load saved login data (simulate localStorage)
onMounted(() => {
  // You could load saved login preferences here
  // const savedData = localStorage.getItem('defaultLogin')
  // if (savedData) {
  //   const data = JSON.parse(savedData)
  //   email.value = data.email || ''
  //   // ... other saved data
  // }
})
</script>

<style scoped>
.auth-logo {
  position: absolute;
  top: 2rem;
  left: 2rem;
  z-index: 6;
}

.auth-wrapper {
  min-height: 100vh;
}

.auth-card-v2 {
  background-color: rgb(var(--v-theme-surface));
}

.login-option-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.login-option-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.3);
  transform: translateY(-2px);
}

.login-option-card.selected {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.auth-footer-mask {
  width: 100%;
  height: 100%;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .auth-logo {
    position: relative;
    top: auto;
    left: auto;
    justify-content: center;
    padding: 1rem;
  }
}
</style>
